import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { getSEO } from "@/lib/seo-config";
import { PERSONA_TYPES } from "@/lib/persona-types";
import {
  Home, Compass, Building2, Building, Landmark, HardHat, Wallet,
  Users, Briefcase, ArrowRight, ShieldCheck, Lock, Camera, FileText,
  Sparkles, CheckCircle2, Scale, Award, MapPin,
} from "lucide-react";

const Overline = ({ children, color = "#FF4D00" }) => (
  <div className="mono text-[11px] uppercase tracking-widest font-semibold" style={{ color }}>{children}</div>
);

const PERSONAS = [
  {
    id: "particulier",
    icon: Home,
    label: "Particulier",
    seo: "Particulier · rénovation maison & appartement",
    baseline: "Vous rénovez votre chez-vous.",
    pitch: "Cuisine, salle de bain, ravalement, extension… Batisur protège chaque euro versé jusqu'à ce que le chantier soit terminé et validé par vous.",
    highlights: [
      "Escrow Stripe : votre argent est bloqué, jamais perdu",
      "SIRET vérifié pour chaque artisan (INSEE)",
      "Preuves photo/vidéo horodatées avant de libérer les fonds",
      "Médiation neutre en cas de désaccord",
    ],
    example: "Sophie, Lyon 3e — a fait refaire sa salle de bain (12 m²) via Batisur. Budget séquestré : 8 400€. Libéré après validation avec 34 photos horodatées.",
  },
  {
    id: "architecte",
    icon: Compass,
    label: "Architecte",
    seo: "Architectes DPLG · maîtrise d'œuvre",
    baseline: "Vous pilotez les artisans, pas leur trésorerie.",
    pitch: "Coordonnez vos lots sans jouer les banquiers. Batisur séquestre les fonds du client, gère les acomptes par phase et centralise les preuves d'avancement.",
    highlights: [
      "Découpe par lots et acomptes par phase de chantier",
      "Journal partagé avec vos artisans et votre client",
      "Historique opposable pour vos assurances RC & décennale",
      "Facturation Factur-X 2026 automatique",
    ],
    example: "Cabinet Bertholet & Fils — supervise 8 chantiers/an sur Batisur. Zéro impayé, 40h/mois gagnées sur la gestion administrative.",
  },
  {
    id: "promoteur",
    icon: Building2,
    label: "Promoteur immobilier",
    seo: "Promoteurs & lotisseurs · programmes neufs",
    baseline: "Vos artisans livrent sans vous relancer.",
    pitch: "Sécurisez vos sous-traitants sur chaque tranche de programme. Les fonds sont provisionnés, la levée se fait sur PV de réception numérique.",
    highlights: [
      "Séquestre par tranche / macro-lot",
      "Preuves d'avancement horodatées géolocalisées",
      "Reporting temps réel pour vos actionnaires",
      "Factur-X 2026 pour votre chaîne comptable",
    ],
    example: "SCCV Riviera Green — 32 lots sécurisés via Batisur sur un programme de 4 M€. Zéro retard de paiement, zéro contentieux.",
  },
  {
    id: "cmi",
    icon: HardHat,
    label: "Constructeur (CMI)",
    seo: "Constructeurs de maisons individuelles · CCMI",
    baseline: "Vos clients réservés. Vos artisans payés.",
    pitch: "Ajoutez une couche de confiance à votre CCMI. Vos acquéreurs voient l'argent bougé, vos sous-traitants voient l'argent arriver.",
    highlights: [
      "Compatibilité avec vos jalons CCMI (5%, 25%, 40%…)",
      "Preuves photos indispensables aux banques prêteuses",
      "Facturation TVA 5.5% / 10% / 20% automatique",
      "Traçabilité opposable pour les recours décennaux",
    ],
    example: "Maisons Provence Construction — sécurise l'intégralité de sa sous-traitance sur Batisur. Décrochage acquéreurs divisé par 3.",
  },
  {
    id: "syndic",
    icon: Landmark,
    label: "Syndic de copropriété",
    seo: "Syndics · assemblées générales & travaux votés",
    baseline: "Votes AG respectés au centime près.",
    pitch: "Les copropriétaires voient exactement où va l'argent voté. L'assemblée générale n'est plus un chèque en blanc.",
    highlights: [
      "Traçabilité opposable pour les copropriétaires",
      "Répartition automatisée sur appels de fonds",
      "Preuves photo à joindre au PV d'AG",
      "Historique par lot pour les mutations",
    ],
    example: "Syndic Foncia Paris 17e — a piloté un ravalement 350k€ avec Batisur. Zéro contestation en AG l'année suivante.",
  },
  {
    id: "bailleur",
    icon: Building,
    label: "Bailleur / Foncière",
    seo: "Bailleurs sociaux & foncières privées",
    baseline: "Vos travaux de rénovation, prouvés locataire par locataire.",
    pitch: "Rénovation énergétique, remise en état, gros entretien : Batisur donne à vos artisans un cadre clair et à vos bailleurs sociaux la preuve exigée.",
    highlights: [
      "Un journal par logement / par lot",
      "TVA 5.5% travaux énergétiques applicable",
      "Certificat de fin de travaux téléchargeable",
      "Export CSV pour votre reporting DPE",
    ],
    example: "Foncière Habitat Sud — rénove 120 logements/an sur Batisur. Preuve photo devenue standard interne.",
  },
  {
    id: "entreprise_btp",
    icon: Briefcase,
    label: "Entreprise BTP",
    seo: "Entreprises générales · gros œuvre & second œuvre",
    baseline: "Vos sous-traitants tenus par un contrat vivant.",
    pitch: "Vous êtes maître d'œuvre. Vos sous-traitants savent que le budget est là, vous savez qu'ils livrent avant d'être payés. La confiance devient contractuelle.",
    highlights: [
      "Multi-lots gérés en un seul chantier",
      "Avenants signés + fonds ajustés en temps réel",
      "Sous-traitance loi 1975 respectée",
      "Factur-X 2026 côté client final",
    ],
    example: "GEB Construction — 14 sous-traitants sur un même chantier gérés via Batisur, marge nette +12%.",
  },
  {
    id: "collectivite",
    icon: Landmark,
    label: "Collectivité",
    seo: "Communes & marchés publics travaux",
    baseline: "Le marché public dans un cadre 100% traçable.",
    pitch: "Écoles, gymnases, voirie : donnez à vos élus et à la Cour des comptes une traçabilité sans faille. Batisur devient votre coffre-fort numérique.",
    highlights: [
      "Traçabilité niveau contrôle Cour des comptes",
      "Preuves horodatées géolocalisées",
      "Compatible bons de commande & marché à procédure adaptée",
      "Export pour rapport annuel d'exécution",
    ],
    example: "Commune de Meylan (38) — a piloté 4 marchés publics travaux via Batisur. Zéro recours contentieux.",
  },
  {
    id: "maitre_oeuvre",
    icon: Compass,
    label: "Maître d'œuvre",
    seo: "MOE indépendants · coordination chantiers",
    baseline: "Un tableau de bord unique pour vos 12 chantiers.",
    pitch: "Vous coordonnez plusieurs artisans sur plusieurs opérations ? Batisur centralise devis, jalons, preuves et factures. Vous facturez votre honoraire au-dessus.",
    highlights: [
      "Vue multi-chantiers par client",
      "Journal partagé entre tous les intervenants",
      "Alertes messagerie temps réel",
      "Vos factures MOE générées Factur-X",
    ],
    example: "Studio Archi Nomade — pilote 12 chantiers/an sur Batisur. Gain estimé : 15h/semaine sur relances.",
  },
  {
    id: "investisseur",
    icon: Wallet,
    label: "Investisseur locatif",
    seo: "Investisseurs immobiliers · rénovation locative",
    baseline: "Vos travaux à distance, vos preuves opposables.",
    pitch: "Vous achetez un bien à Metz, vous vivez à Bordeaux ? Batisur devient vos yeux sur le chantier. Photos horodatées, factures conformes, garantie décennale traçable.",
    highlights: [
      "Chantier suivi à distance en temps réel",
      "Preuves opposables pour votre notaire",
      "Facturation Factur-X pour votre déclaration foncière",
      "TVA 10% ou 5.5% applicable auto",
    ],
    example: "Karim, 8 lots locatifs — pilote tous ses travaux Batisur depuis son téléphone. 3 chantiers menés en 2025, zéro déplacement.",
  },
];

const injectJSONLD = (data) => {
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = "for-who-jsonld";
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
  return () => { const e = document.getElementById("for-who-jsonld"); if (e) e.remove(); };
};

// FAQPage schema built from PERSONAS for enhanced Google results
const forWhoFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": PERSONAS.map((p) => ({
    "@type": "Question",
    "name": `Batisur est-il adapté aux ${p.label.toLowerCase()} ?`,
    "acceptedAnswer": { "@type": "Answer", "text": p.pitch },
  })),
};

export default function ForWho() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#F7F7F9]" data-testid="for-who-page">
      <SEO cfg={{ ...getSEO("/pour-qui"), schema: [forWhoFAQSchema] }} />
      <Navbar />

      {/* HERO */}
      <section className="relative border-b-2 border-[#0A0A0B] bg-white overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 relative">
          <div className="inline-flex items-center gap-2 border-2 border-[#0A0A0B] bg-white px-3 py-1.5 mono text-[11px] uppercase tracking-widest mb-8 shadow-[4px_4px_0_#0A0A0B]">
            <span className="w-2 h-2 bg-[#FF4D00]" /> Pour tous les donneurs d'ordre BTP
          </div>
          <h1 className="display text-[clamp(2rem,7vw,5rem)] tracking-tighter leading-[0.95] max-w-5xl">
            10 profils. <span className="text-[#FF4D00]">1 seule</span> plateforme.<br />
            <span className="underline decoration-[#FF4D00] decoration-[6px] underline-offset-4">Zéro impayé.</span>
          </h1>
          <p className="mt-8 text-lg text-zinc-700 max-w-3xl">
            Que vous soyez particulier en rénovation, architecte pilote, promoteur, syndic, bailleur ou investisseur locatif —
            Batisur Connect adapte l'escrow, la facturation et les preuves opposables à votre métier.
          </p>

          {/* Quick nav — with SEO link to dedicated persona page */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-2 max-w-5xl">
            {PERSONAS.map((p) => (
              <div key={p.id} className="flex flex-col border-2 border-[#0A0A0B] bg-white hover:bg-[#0A0A0B] hover:text-white transition-colors">
                <button
                  onClick={() => scrollTo(p.id)}
                  className="p-3 text-left"
                  data-testid={`persona-nav-${p.id}`}
                >
                  <p.icon className="w-5 h-5 text-[#FF4D00] mb-1" />
                  <div className="mono text-[10px] uppercase tracking-widest">{p.label}</div>
                </button>
                {PERSONA_TYPES[p.id] && (
                  <Link
                    to={`/pour/${p.id}`}
                    className="mono text-[9px] uppercase tracking-widest px-3 py-2 border-t border-current bg-[#FF4D00] text-white hover:bg-[#0A0A0B] transition"
                    data-testid={`persona-detail-link-${p.id}`}
                  >
                    Page dédiée →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO — Vous cherchez / Vous avez … */}
      <section className="border-b-2 border-[#0A0A0B] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <Overline>// Vous êtes demandeur et…</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-4xl">Trouvez un artisan de confiance et sécurisez vos travaux du bâtiment.</h2>
          <p className="mt-4 text-lg text-zinc-700 max-w-3xl">
            Que votre projet soit une <strong>rénovation de maison, une extension, un ravalement de façade, une réfection de toiture, une salle de bain, une cuisine équipée, une isolation thermique par l'extérieur (ITE), une rénovation énergétique, un aménagement de combles, une construction neuve ou de gros œuvre</strong> — Batisur Connect vous met en relation avec des artisans BTP vérifiés SIRET et vous garantit un paiement 100% sécurisé.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {[
              {
                q: "Vous cherchez un artisan sérieux et vérifié ?",
                icon: ShieldCheck,
                body: "Chaque artisan Batisur est <strong>vérifié SIRET auprès de l'INSEE Sirene</strong>. Vous accédez à un annuaire de plombiers, électriciens, maçons, peintres, carreleurs, chauffagistes, couvreurs, menuisiers et façadiers avec avis clients réels, note moyenne, historique de chantiers réalisés et zone d'intervention. Filtrez par ville, spécialité, badge Or ou Premium.",
              },
              {
                q: "Vous avez un projet de rénovation et vous voulez un devis clair ?",
                icon: FileText,
                body: "Publiez votre demande gratuitement en 5 minutes : catégorie de travaux, surface, budget, ville, deadline. Vous recevez des <strong>devis fermes et détaillés</strong>, comparables entre eux. Batisur formalise chaque devis en PDF opposable. Estimez d'abord votre budget grâce à notre <a href='/estimer' style='color:#FF4D00'>estimateur de coût gratuit</a>.",
              },
              {
                q: "Vous voulez être sûr que vos travaux seront livrés ?",
                icon: Camera,
                body: "Chaque artisan Batisur documente son chantier via un <strong>journal photo/vidéo horodaté</strong> avec GPS et watermark irrévocable. Vous suivez l'avancement en temps réel depuis votre téléphone. À la fin du chantier, vous validez visuellement chaque étape avant de libérer les fonds. Fini les chantiers abandonnés, les malfaçons cachées et les 'à la semaine prochaine'.",
              },
              {
                q: "Vous voulez payer sans risque d'arnaque ?",
                icon: Lock,
                body: "Le budget est <strong>séquestré chez Stripe</strong> (leader mondial du paiement) avant le démarrage. L'artisan sait que l'argent est bloqué et prêt. Vous savez que l'argent ne partira qu'après votre validation. En cas de litige, l'admin Batisur arbitre en médiation neutre sur pièces (journal photo, échanges, devis). C'est la fin des acomptes qui disparaissent avec l'artisan.",
              },
              {
                q: "Vous voulez une facture conforme pour vos aides et votre déclaration ?",
                icon: Award,
                body: "Toutes les factures Batisur sont générées au format <strong>Factur-X 2026 (PDF/A-3 + XML EN 16931)</strong>. TVA à 5,5%, 10% ou 20% appliquée automatiquement selon les travaux (rénovation énergétique, entretien, neuf). Idéal pour votre déclaration d'impôts, MaPrimeRénov', CEE, éco-PTZ, TVA réduite, ou déclaration foncière pour investisseur locatif.",
              },
              {
                q: "Vous êtes à distance et vous voulez piloter votre chantier ?",
                icon: MapPin,
                body: "Que vous soyez propriétaire à Paris avec un bien à Marseille, expatrié pilotant une rénovation en France, ou architecte gérant plusieurs chantiers simultanés — Batisur centralise <strong>photos, factures, messagerie et validations</strong> dans un tableau de bord unique. Vous pilotez votre chantier depuis n'importe où, 24/7, avec preuves opposables.",
              },
            ].map(({ q, icon: Icon, body }, i) => (
              <article key={q} className="border-2 border-[#0A0A0B] bg-[#F7F7F9] p-6 hover:bg-white transition-colors" data-testid={`demandeur-seo-block-${i}`}>
                <Icon className="w-6 h-6 text-[#FF4D00]" />
                <h3 className="display text-2xl mt-3 leading-tight">{q}</h3>
                <p className="mt-4 text-sm text-zinc-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: body }} />
              </article>
            ))}
          </div>

          <div className="mt-12 border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8">
            <Overline color="#FF4D00">// Mots-clés Batisur pour demandeurs</Overline>
            <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
              Trouver un artisan de confiance · Comparer des devis rénovation · Rénover sa maison sans se faire arnaquer · Paiement sécurisé travaux · Escrow rénovation · Devis plombier électricien maçon peintre · Artisan vérifié SIRET · Rénovation énergétique aidée · Extension maison budget · Toiture ravalement façade · Cuisine salle de bain clé en main · Litige artisan preuves · Journal chantier horodaté · Facture Factur-X · TVA 5,5% rénovation · Suivi travaux à distance · Investissement locatif rénovation.
            </p>
          </div>

          <div className="mt-8 text-center">
            <Link
              to="/estimer"
              className="btn-anthracite inline-flex items-center gap-2"
              data-testid="demandeur-seo-estimator-cta"
            >
              Estimer gratuitement mon projet <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PERSONAS */}
      <div className="max-w-7xl mx-auto px-6 py-16 space-y-16">
        {PERSONAS.map((p, i) => (
          <section
            key={p.id}
            id={p.id}
            className="grid lg:grid-cols-12 gap-10 items-start scroll-mt-24"
            data-testid={`persona-${p.id}`}
          >
            <div className="lg:col-span-5 lg:sticky lg:top-24">
              <div className="border-2 border-[#0A0A0B] bg-[#0A0A0B] text-white p-8 shadow-[8px_8px_0_#FF4D00]">
                <p.icon className="w-10 h-10 text-[#FF4D00]" />
                <Overline color="#FF4D00">// Persona {String(i + 1).padStart(2, "0")}</Overline>
                <div className="display text-4xl mt-3">{p.label}</div>
                <div className="mono text-[10px] uppercase tracking-widest text-zinc-500 mt-2">{p.seo}</div>
                <p className="mt-6 text-lg text-zinc-200 leading-snug">{p.baseline}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  <Link
                    to={`/register?role=demandeur&type=${p.id}`}
                    className="btn-batisur text-xs"
                    data-testid={`persona-cta-${p.id}`}
                  >
                    Je suis {p.label.toLowerCase()} →
                  </Link>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-zinc-700 leading-relaxed">{p.pitch}</p>

              <div className="mt-6 border-2 border-[#0A0A0B] bg-white">
                <div className="p-5 border-b-2 border-[#0A0A0B] bg-[#FF4D00] text-white">
                  <Overline color="#fff">// Ce que Batisur apporte à un {p.label.toLowerCase()}</Overline>
                </div>
                <ul className="p-6 space-y-3">
                  {p.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-3 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-[#FF4D00] shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 border-l-4 border-[#FF4D00] pl-5 py-3 bg-zinc-50">
                <Overline>// Cas concret</Overline>
                <p className="mt-2 text-sm text-zinc-700 italic">{p.example}</p>
              </div>
            </div>
          </section>
        ))}
      </div>

      {/* WHY UNIVERSAL */}
      <section className="border-y-2 border-[#0A0A0B] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <Overline>// Le socle commun</Overline>
          <h2 className="display text-4xl lg:text-5xl mt-3 max-w-3xl">
            Peu importe qui vous êtes, la mécanique est la même.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#0A0A0B]">
            {[
              [Lock, "Escrow Stripe", "Fonds bloqués avant le premier coup de marteau."],
              [Camera, "Preuves horodatées", "Photo/vidéo avec GPS, hash et watermark irrévocable."],
              [FileText, "Factur-X 2026", "PDF/A-3 + XML EN 16931, conforme à la réforme B2B."],
              [Scale, "Médiation neutre", "L'admin Batisur arbitre les litiges sur pièces."],
            ].map(([Icon, t, d], i) => (
              <div key={t} className={`p-6 bg-white ${i < 3 ? "border-r-2" : ""} ${i < 2 ? "lg:border-b-0 border-b-2 lg:border-r-2" : ""} border-[#0A0A0B]`}>
                <Icon className="w-7 h-7 text-[#FF4D00]" />
                <div className="display text-xl mt-3">{t}</div>
                <div className="text-sm text-zinc-600 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MEDIATEUR À VOS CÔTÉS */}
      <section className="border-b-2 border-[#0A0A0B] bg-[#0A0A0B] text-white overflow-hidden relative" data-testid="mediator-section">
        <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-20 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5">
              <Overline color="#FF4D00">// Vous n'êtes jamais seul</Overline>
              <h2 className="display text-4xl lg:text-5xl mt-3 leading-[0.95]">
                Un <span className="text-[#FF4D00]">médiateur</span> Batisur<br />à vos côtés<br />jusqu'à la fin.
              </h2>
              <p className="mt-6 text-lg text-zinc-300 leading-relaxed">
                Dès votre <strong className="text-white">devis signé</strong> et les <strong className="text-white">fonds séquestrés sur Stripe</strong>,
                un médiateur dédié Batisur Connect vous accompagne :
                depuis le paiement jusqu'à la réception finale des travaux.
              </p>
              <p className="mt-4 text-sm text-zinc-400">
                Ce n'est pas un chatbot. C'est un humain, joignable, qui a accès à votre journal de chantier,
                vos échanges et votre séquestre. Son objectif : que votre projet aboutisse sans stress.
              </p>
            </div>

            <div className="lg:col-span-7 grid md:grid-cols-2 gap-4">
              {[
                {
                  icon: Lock,
                  title: "Étape 1 · Devis signé",
                  body: "À l'instant où vous acceptez un devis, votre dossier est ouvert côté médiation. Un premier email récapitulatif vous est envoyé avec le nom de votre médiateur.",
                },
                {
                  icon: ShieldCheck,
                  title: "Étape 2 · Fonds séquestrés",
                  body: "Le paiement Stripe est confirmé. Votre budget est bloqué en compte séquestre — ni l'artisan ni Batisur n'y a accès. Votre médiateur veille sur le déclencheur de libération.",
                },
                {
                  icon: Camera,
                  title: "Étape 3 · Chantier en cours",
                  body: "Le médiateur suit chaque photo horodatée du journal. Si un jalon prend du retard ou qu'un doute apparaît, il vous alerte proactivement.",
                },
                {
                  icon: CheckCircle2,
                  title: "Étape 4 · Réception et libération",
                  body: "Une fois la photo de Finition publiée par l'artisan et votre validation reçue, votre médiateur déclenche la libération Stripe et vous délivre votre certificat de fin de travaux.",
                },
              ].map(({ icon: Icon, title, body }, i) => (
                <div key={title} className="border-2 border-white/20 bg-white/5 p-5 hover:bg-white/10 transition-colors backdrop-blur" data-testid={`mediator-step-${i}`}>
                  <Icon className="w-7 h-7 text-[#FF4D00]" />
                  <div className="mono text-[10px] uppercase tracking-widest text-[#FF4D00] mt-3">{title}</div>
                  <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 border-2 border-[#FF4D00] p-6 bg-[#FF4D00]/10">
            <div className="flex items-start gap-4">
              <Scale className="w-8 h-8 text-[#FF4D00] shrink-0" />
              <div>
                <div className="display text-xl">En cas de désaccord ? Médiation amiable gratuite en 30 jours.</div>
                <p className="text-sm text-zinc-300 mt-2">
                  Si un litige survient (retard, malfaçon, refus de validation), votre médiateur ouvre un dossier. Il analyse le journal photo, les échanges, le devis, et propose un accord neutre aux deux parties. Ce service est <strong className="text-white">100% gratuit et conforme à nos CGU (article 6.2)</strong>. À défaut d'accord, les fonds restent bloqués jusqu'à décision de justice — vous êtes protégé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0B] text-white border-b-2 border-[#0A0A0B]">
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <Sparkles className="w-10 h-10 text-[#FF4D00] mx-auto" />
          <h2 className="display text-4xl lg:text-5xl mt-4">Votre premier chantier sécurisé commence ici.</h2>
          <p className="mt-4 text-lg text-zinc-400">Inscription gratuite. 0€ si aucune mission ne démarre.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register?role=demandeur" className="btn-batisur" data-testid="forwho-cta-register">
              Créer mon compte demandeur →
            </Link>
            <Link to="/estimer" className="border-2 border-white text-white font-semibold px-6 py-3 mono text-xs uppercase tracking-widest hover:bg-white hover:text-[#0A0A0B]" data-testid="forwho-cta-estimer">
              Estimer mes travaux
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
